import React from "react";
import { ExternalLink } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../misc/Card";
import { PlatformIcon } from "../../utils/logo-getter";
import { AnalyticsRecentPost } from "../../types/analytics";
import { formatPublishedAt, platformLabel } from "./labels";

interface RecentPostsListProps {
    posts: AnalyticsRecentPost[];
    timeZone: string;
}

const ROW_CLASS = "flex items-start gap-3 py-3";

const RowBody: React.FC<{ post: AnalyticsRecentPost; timeZone: string }> = ({ post, timeZone }) => (
    <>
        <span className="w-6 h-6 shrink-0 mt-0.5">
            <PlatformIcon platform={post.platform} className="w-6 h-6" />
        </span>
        <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-500">
                {formatPublishedAt(post.publishedAt, timeZone)} &middot; {platformLabel(post.platform)}
                {post.postType ? ` · ${post.postType}` : ""}
                {post.mediaKind === "video" ? " · Video" : ""}
            </div>
            {post.excerpt && (
                <p className="mt-0.5 text-sm text-primary line-clamp-2">{post.excerpt}</p>
            )}
        </div>
    </>
);

const RecentPostsList: React.FC<RecentPostsListProps> = ({ posts, timeZone }) => (
    <Card className="mt-0">
        <CardHeader className="pb-2">
            <CardTitle className="text-left text-base">Recent posts</CardTitle>
        </CardHeader>
        <CardContent>
            {posts.length === 0 ? (
                <p className="text-sm text-gray-500">Nothing published in this window yet.</p>
            ) : (
                <ul className="divide-y divide-gray-200">
                    {posts.map((post) =>
                        post.postUrl ? (
                            // The whole row is the link, so a click anywhere on it
                            // opens the live post; "View post" stays as the cue.
                            <li key={post.postId}>
                                <a
                                    href={post.postUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={
                                        post.excerpt
                                            ? `View post: ${post.excerpt}`
                                            : `View ${platformLabel(post.platform)} post`
                                    }
                                    className={`${ROW_CLASS} group -mx-2 rounded-md px-2 hover:bg-gray-50`}
                                >
                                    <RowBody post={post} timeZone={timeZone} />
                                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:underline shrink-0">
                                        View post <ExternalLink className="h-4 w-4" />
                                    </span>
                                </a>
                            </li>
                        ) : (
                            <li key={post.postId} className={ROW_CLASS}>
                                <RowBody post={post} timeZone={timeZone} />
                            </li>
                        ),
                    )}
                </ul>
            )}
        </CardContent>
    </Card>
);

export default RecentPostsList;
